import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get, isEmpty, trim } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputCheckboxField } from "../../../../components/Shared/InputCheckboxField";
import { InputTextAreaField } from "../../../../components/Shared/InputTextAreaField";
import { request } from "../../../../utils/Request";
import { Divider } from "primereact/divider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import Auth from "../../../../utils/Auth";
import DOMPurify from "dompurify";
import { prepareChatParticipants } from "../../../../components/Chat/helpers/participants";
import { Message, Participant } from "../../../../components/Chat/types";
import {
    getDamageCaseFirebaseClient,
    resolveDamageCaseFirebaseProvider,
} from "../../../../utils/damageCaseFirebase";

interface IProps {
    visible: boolean;
    toastify: any;
    damageCase: any;
    onClose: any;
}

type TInputForm = {
    comments: string;
    send_notification_efl: boolean;
    send_notification_carrier: boolean;
    send_notification_compound: boolean;
    send_notification_dekra: boolean;
};

const DEFAULT_FORM_STATE = {
    comments: '',
    send_notification_efl: false,
    send_notification_carrier: false,
    send_notification_compound: false,
    send_notification_dekra: false,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        comments: yup.string().required(t('the field is required.', { field: t('comments') })),
    });
};

function OrderDamageCaseCodeUpdate(props: IProps) {
    const { toastify, damageCase } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [open, setOpen] = useState<boolean>(false);
    const [caseStatus, setCaseStatus] = useState<any[]>([]);
    const { t }: any = useTranslation();

    useEffect(() => {
        if ( props.visible && damageCase ) {
            setOpen(true);
            setInitFormState(DEFAULT_FORM_STATE);

            // Initialize case status from damage codes
            const damageCodes = get(damageCase, ['damage_codes']) || [];
            const initialStatus = damageCodes.map((code: any) => ( {
                id: get(code, ['emil_frey_id']),
                code: get(code, ['code']),
                description: get(code, ['description']),
                carrier_status: get(code, ['carrier_status']),
                original_status: get(code, ['carrier_status']),
            } ));
            setCaseStatus(initialStatus);
        } else if ( !props.visible ) {
            setOpen(false);
        }
    }, [props.visible, damageCase]);

    const onClose = () => {
        setOpen(false);
        props.onClose();
    };

    /**
     * Sanitize HTML content to prevent XSS attacks
     */
    const sanitizeMessage = (html: string): string => {
        if ( !html ) {
            return '';
        }
        const config = {
            ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'p', 'br', 'span', 'div'],
            ALLOWED_ATTR: ['href', 'target', 'rel'],
            ALLOW_DATA_ATTR: false,
        };
        return DOMPurify.sanitize(html, config);
    };

    /**
     * Send comment message to Firebase chat for rejected damage codes
     */
    const sendRejectedCodeCommentsToFirebase = async (commentText: string, rejectedCodes: any[]) => {
        if ( isEmpty(trim(commentText)) || rejectedCodes.length === 0 ) {
            return;
        }

        const auth = new Auth();
        const user: any = auth.getUser();
        if ( !user?.email ) {
            return;
        }

        // Get sender email with alpc_ prefix
        let senderEmail = user.email || '';
        if ( !senderEmail.startsWith('alpc_') ) {
            senderEmail = `alpc_${ senderEmail }`;
        }

        // Get sender label
        const senderLabel = user?.name ||
            ( user?.first_name && user?.last_name
                ? `${ user.first_name } ${ user.last_name }`
                : user?.email || 'Unknown' );

        const sanitizedComment = sanitizeMessage(commentText);
        const firebaseClient = getDamageCaseFirebaseClient(
            resolveDamageCaseFirebaseProvider(get(damageCase, ['source_type']))
        );

        const caseDataForParticipants = {
            ...damageCase,
            participants: get(damageCase, ['participants']) || [],
        };

        const masterUser = null; // Can be passed via props if needed

        const firebasePromises = rejectedCodes.map(async (rejectedCode: any) => {
            const chatToken = String(rejectedCode.emil_frey_id || rejectedCode.id); // emil_frey_id is the chat token

            try {
                const participants = prepareChatParticipants(
                    caseDataForParticipants,
                    [], // No Firestore participants initially
                    masterUser,
                    [] // No initial participants from props
                );

                const uniqueId = `${ Date.now() }_${ Math.floor(Math.random() * 10000) }_${ rejectedCode.id }`;

                const newMessage: Message = {
                    id: uniqueId,
                    message: sanitizedComment,
                    time: new Date().toISOString(),
                    type: 0,
                    is_file: 0,
                    name: senderLabel,
                    email: senderEmail,
                };

                const chatRef = firebaseClient.firestore.collection('chats').doc(chatToken);
                const chatDoc = await chatRef.get();

                if ( chatDoc.exists ) {
                    // Update existing chat document
                    await chatRef.update({
                        messages: firebaseClient.firebase.firestore.FieldValue.arrayUnion(newMessage),
                        // Also sync participants if new ones are added from backend
                        participants: firebaseClient.firebase.firestore.FieldValue.arrayUnion(...participants.map((p: Participant) => ( {
                            email: p.email,
                            label: p.label || p.email,
                            id: p.id,
                        } ))),
                    });
                } else {
                    // Create new chat document
                    await chatRef.set({
                        messages: [newMessage],
                        participants: participants.map((p: Participant) => ( {
                            email: p.email,
                            label: p.label || p.email,
                            id: p.id,
                        } )),
                        created_at: firebaseClient.firebase.firestore.FieldValue.serverTimestamp(),
                    });
                }
            } catch (error) {
                console.error(`Error sending message to chat ${ chatToken }:`, error);
            }
        });

        await Promise.allSettled(firebasePromises);
    };

    const onSubmit = async (payloads: TInputForm) => {
        // Send all damage codes with their current carrier_status
        const damageCodes = caseStatus.map((code: any) => ( {
            id: code.id,
            carrier_status: code.carrier_status,
        } ));

        try {
            await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${ get(damageCase, ['id']) }/update/damage/codes`,
                data: {
                    comments: payloads.comments,
                    send_notification_efl: payloads.send_notification_efl || false,
                    send_notification_carrier: payloads.send_notification_carrier || false,
                    send_notification_compound: payloads.send_notification_compound || false,
                    send_notification_dekra: payloads.send_notification_dekra || false,
                    damage_codes: damageCodes,
                    type: "code-status-update"
                },
            });

            // Send comments to Firebase for rejected codes
            const rejectedCodes = caseStatus.filter((code: any) => {
                const updatedCode = damageCodes.find((dc: any) => dc.id === code.id);
                return updatedCode && updatedCode.carrier_status === 'rejected';
            });
            if ( rejectedCodes.length > 0 && !isEmpty(trim(payloads.comments)) ) {
                // Send to Firebase asynchronously (don't block the UI)
                sendRejectedCodeCommentsToFirebase(payloads.comments, rejectedCodes).catch((error) => {
                    console.error('Error sending comments to Firebase:', error);
                });
            }

            toastify(t("record updated", { item: t('damage case') }), "info");
            onClose();
        } catch (e: any) {
            if ( e.status === 422 && e.data && e.data.errors ) {
                toastify(t("validation error"), "error");
            } else if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }
        setInitFormState(DEFAULT_FORM_STATE);

        // Reset case status to original
        const damageCodes = get(damageCase, ['damage_codes']) || [];
        const initialStatus = damageCodes.map((code: any) => ( {
            id: get(code, ['id']),
            emil_frey_id: get(code, ['emil_frey_id']),
            code: get(code, ['code']),
            description: get(code, ['description']),
            carrier_status: get(code, ['carrier_status']),
            original_status: get(code, ['carrier_status']),
        } ));
        setCaseStatus(initialStatus);
    };

    const handleStatusUpdate = (id: number, newStatus: string) => {
        setCaseStatus((prev: any) =>
            prev.map((code: any) =>
                code.id === id ? { ...code, carrier_status: newStatus } : code
            )
        );
    };


    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className="card-title mt-0">
                                { get(damageCase, ['case_number']) ? t('update damage codes') + ' #' + get(damageCase, ['case_number']) : t('update damage codes') }
                            </h5>
                            <p>{ t("update damage code responsibility status.") }</p>
                        </div>
                    }
                    visible={ open }
                    position="left"
                    onHide={ onClose }
                    blockScroll={ true }
                    style={ { width: '900px' } }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (formProps: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ formProps.handleSubmit } className="mb-5">
                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={ 12 }>
                                            <h6 className="mb-3">{ t('damage codes') }</h6>
                                            <DataTable
                                                value={ caseStatus }
                                                dataKey="id"
                                                className="datatable-responsive"
                                                emptyMessage={ t('no data') }
                                            >
                                                <Column
                                                    field="code"
                                                    header={ t('damage code') }
                                                    body={ (rowData: any) => (
                                                        <div>
                                                            <div><strong>{ rowData.code }</strong></div>
                                                            <div
                                                                className="text-muted small">{ rowData.description }</div>
                                                        </div>
                                                    ) }
                                                />
                                                <Column
                                                    field="carrier_status"
                                                    header={ t('status') }
                                                    body={ (rowData: any) => (
                                                        <Tag
                                                            value={ rowData.carrier_status }
                                                            severity={ rowData.carrier_status === 'accepted' ? 'success' : rowData.carrier_status === 'rejected' ? 'danger' : 'warning' }
                                                            className="text-capitalize"
                                                        />
                                                    ) }
                                                    style={ { width: '150px' } }
                                                />
                                                <Column
                                                    field="action"
                                                    header={ t('action') }
                                                    body={ (rowData: any) => {
                                                        const isPending = rowData.original_status === 'pending';
                                                        const isRejected = rowData.original_status === 'rejected';
                                                        const canUpdate = isPending || isRejected;

                                                        if ( !canUpdate ) {
                                                            // Read-only status for accepted codes
                                                            return (
                                                                <Tag
                                                                    value={ rowData.carrier_status }
                                                                    severity={ rowData.carrier_status === 'accepted' ? 'success' : rowData.carrier_status === 'rejected' ? 'danger' : 'warning' }
                                                                    className="text-capitalize"
                                                                />
                                                            );
                                                        }

                                                        return (
                                                            <div className="d-flex gap-1">
                                                                <Tooltip tooltip={ t('accept') }>
                                                                    <Button
                                                                        type="button"
                                                                        icon="pi pi-check"
                                                                        size="small"
                                                                        severity={ rowData.carrier_status === 'accepted' ? 'success' : 'secondary' }
                                                                        outlined={ rowData.carrier_status !== 'accepted' }
                                                                        onClick={ () => handleStatusUpdate(rowData.id, 'accepted') }
                                                                    />
                                                                </Tooltip>
                                                                { isPending && (
                                                                    <Tooltip tooltip={ t('reject') }>
                                                                        <Button
                                                                            type="button"
                                                                            icon="pi pi-times"
                                                                            size="small"
                                                                            severity={ rowData.carrier_status === 'rejected' ? 'danger' : 'secondary' }
                                                                            outlined={ rowData.carrier_status !== 'rejected' }
                                                                            onClick={ () => handleStatusUpdate(rowData.id, 'rejected') }
                                                                        />
                                                                    </Tooltip>
                                                                ) }
                                                            </div>
                                                        );
                                                    } }
                                                    style={ { width: '200px' } }
                                                />
                                            </DataTable>
                                        </Col>
                                    </Row>

                                    <Divider />

                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={ 12 }>
                                            <label className="form-label">{ t("send notification to") }</label>
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_efl"
                                                label={ t("efl") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_carrier"
                                                label={ t("carrier") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_compound"
                                                label={ t("compound") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_dekra"
                                                label={ t("dekra") }
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={ 12 }>
                                            <Field
                                                component={ InputTextAreaField }
                                                name="comments"
                                                label={ t("comments") }
                                                placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                                rows={ 4 }
                                                isRequired
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row>
                                        <Col sm={ 12 }>
                                            <div className="filter-card-footer" style={ { width: '885px' } }>
                                                <Button
                                                    type="submit"
                                                    disabled={ formProps.isSubmitting }
                                                    tooltip={ t("to data save into database") }
                                                    className="ms-2"
                                                    size="small"
                                                    outlined
                                                    tooltipOptions={ { position: 'top' } }
                                                >
                                                    <i className="pi pi-save me-2" />
                                                    { t("save") }
                                                </Button>
                                                <Button
                                                    type="button"
                                                    disabled={ formProps.isSubmitting }
                                                    onClick={ onReset }
                                                    tooltip={ t("to make all fields empty") }
                                                    className="ms-2"
                                                    size="small"
                                                    severity="warning"
                                                    outlined
                                                    tooltipOptions={ { position: 'top' } }
                                                >
                                                    <i className="pi pi-refresh me-2" />
                                                    { t("reset") }
                                                </Button>
                                                <Button
                                                    type="button"
                                                    tooltip={ t("operation cancel") }
                                                    className="ms-2"
                                                    size="small"
                                                    severity="danger"
                                                    outlined
                                                    tooltipOptions={ { position: 'top' } }
                                                    onClick={ onClose }
                                                >
                                                    <i className="pi pi-arrow-left me-2" />
                                                    { t("cancel") }
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

export default withTranslation()(OrderDamageCaseCodeUpdate);

